import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AxiosInstance from 'src/components/Axios';
import {
	CFormLabel, CFormInput, CFormTextarea, CFormSelect, CFormCheck,
	CButton, CRow, CCol, CCard, CCardHeader, CCardBody, CInputGroup,
} from '@coreui/react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import FormSubmitOverlay from 'src/components/myComponents/FormSubmitOverlay/FormSubmitOverlay';
import NotificationTypeMismatchModal from 'src/components/myComponents/NotificationTypeMismatchModal';
import { isTypeMismatch, detectNotificationType } from 'src/utils/notificationTypeCheck';
import AsyncSelect from 'react-select/async';
import debounce from 'lodash.debounce';

const sendUrl = '/create-notfication';

const TYPE_OPTIONS = [
	{ value: 'text', label: 'Текст' },
	{ value: 'html', label: 'HTML' },
];

const UsersNotificationsForm = () => {
	const navigate = useNavigate();
	const abortControllerRef = useRef(null);

	// Вибір користувача
	const [userInputMode, setUserInputMode] = useState('search'); // 'search' | 'manual'
	const [userId, setUserId] = useState(null);
	const [selectedUser, setSelectedUser] = useState(null);
	const [manualUserId, setManualUserId] = useState('');
	const [confirmedUser, setConfirmedUser] = useState(null); // { username, firstName }
	const [manualConfirming, setManualConfirming] = useState(false);
	const [title, setTitle] = useState('');
	const [body, setBody] = useState('');
	const [type, setType] = useState('text');
	const [submitting, setSubmitting] = useState(false);
	const [errors, setErrors] = useState({});
	const [typeMismatchVisible, setTypeMismatchVisible] = useState(false);
	const pendingSubmitData = useRef(null);

	const handleBack = useCallback(() => navigate(-1), [navigate]);

	// AsyncSelect — пошук користувача
	const fetchUserOptions = useCallback(async (inputValue) => {
		const query = inputValue.trim();
		if (!query || query.length < 3) return [];
		if (abortControllerRef.current) abortControllerRef.current.abort();
		abortControllerRef.current = new AbortController();
		try {
			const response = await AxiosInstance.get(`/user/search?s=${encodeURIComponent(query)}`, {
				signal: abortControllerRef.current.signal,
			});
			return response.data.map((user) => ({
				label: `${user.username}${user.firstName ? ` (${user.firstName})` : ''}`,
				value: user.id,
			}));
		} catch (error) {
			if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return [];
			throw error;
		}
	}, []);

	const debouncedLoadUserOptions = useMemo(() => debounce((inputValue, resolve, reject) => {
		fetchUserOptions(inputValue).then(resolve).catch(reject);
	}, 300), [fetchUserOptions]);

	useEffect(() => () => debouncedLoadUserOptions.cancel(), [debouncedLoadUserOptions]);

	const loadUserOptions = useCallback((inputValue) => {
		return new Promise((resolve, reject) => debouncedLoadUserOptions(inputValue, resolve, reject));
	}, [debouncedLoadUserOptions]);

	const handleAsyncUserChange = (option) => {
		setSelectedUser(option);
		setUserId(option?.value ?? null);
		setConfirmedUser(option ? { username: option.label, firstName: null } : null);
		if (errors.user) setErrors(prev => ({ ...prev, user: false }));
	};

	// Підтвердження ручного ID
	const handleManualConfirm = async () => {
		const numId = parseInt(manualUserId, 10);
		if (!numId || isNaN(numId)) {
			toast.warning('Введіть валідний ID');
			return;
		}
		setManualConfirming(true);
		try {
			const res = await AxiosInstance.get(`/users/${numId}`);
			const user = res.data;
			setUserId(numId);
			setConfirmedUser({ username: user.username, firstName: user.firstName });
			if (errors.user) setErrors(prev => ({ ...prev, user: false }));
		} catch {
			toast.error('Користувача не знайдено');
		} finally {
			setManualConfirming(false);
		}
	};

	const handleClearUser = () => {
		setUserId(null);
		setSelectedUser(null);
		setConfirmedUser(null);
		setManualUserId('');
	};

	const doSend = async (requestData) => {
		// console.log('requsetData', requestData);
		setSubmitting(true);
		try {
			const res = await AxiosInstance.post(sendUrl, requestData);
			// console.log('response', res.data);

			if (res.data.rez === false) {
				toast.error(res.data.error || 'Помилка надсилання');
				return;
			};

			navigate(`/user-notifications/view/${res.data.id}`, { replace: true, state: { toast: { type: 'success', message: 'Сповіщення надіслано' } } });
		} catch (err) {
			toast.error(err.response?.data?.message || 'Помилка надсилання');
		} finally {
			setSubmitting(false);
		}
	};

	const handleSubmit = () => {
		const newErrors = {
			user: !userId,
			title: !title.trim(),
			body: !body.trim(),
		};
		if (newErrors.user || newErrors.title || newErrors.body) {
			setErrors(newErrors);
			const missing = [
				newErrors.user && 'Користувач',
				newErrors.title && 'Заголовок',
				newErrors.body && 'Текст',
			].filter(Boolean);
			toast.warning(`Не заповнено: ${missing.join(', ')}`);
			return;
		}
		setErrors({});

		const requestData = {
			user: userId,
			title: title.trim(),
			body: body.trim(),
			type,
		};

		if (isTypeMismatch(type, body.trim())) {
			pendingSubmitData.current = requestData;
			setTypeMismatchVisible(true);
			return;
		};

		doSend(requestData);
	};

	const handleTypeMismatchConfirm = () => {
		setTypeMismatchVisible(false);
		doSend(pendingSubmitData.current);
		pendingSubmitData.current = null;
	};

	const handleTypeMismatchCancel = () => {
		setTypeMismatchVisible(false);
		pendingSubmitData.current = null;
	};

	const confirmedUserLabel = confirmedUser
		? `${confirmedUser.username}${confirmedUser.firstName ? ` (${confirmedUser.firstName})` : ''}`
		: null;

	return (
		<>
			<ToastContainer />
			<NotificationTypeMismatchModal
				visible={typeMismatchVisible}
				selectedType={type}
				detectedType={detectNotificationType(body)}
				onConfirm={handleTypeMismatchConfirm}
				onCancel={handleTypeMismatchCancel}
			/>
			<CCard className="mb-4">
				<CCardHeader><strong>Нове сповіщення</strong></CCardHeader>
				<CCardBody>
					<FormSubmitOverlay submitting={submitting}>
						<CRow className="g-3">

							{/* Вибір користувача */}
							<CCol xs={12}>
								<CFormLabel>Отримувач *</CFormLabel>

								{!confirmedUser && (
									<div className="d-flex gap-4 mb-2">
										<CFormCheck
											type="radio"
											id="mode-search"
											label="Пошук по імені/username/телефон"
											checked={userInputMode === 'search'}
											onChange={() => setUserInputMode('search')}
										/>
										<CFormCheck
											type="radio"
											id="mode-manual"
											label="Ввести ID вручну"
											checked={userInputMode === 'manual'}
											onChange={() => setUserInputMode('manual')}
										/>
									</div>
								)}

								{!confirmedUser && userInputMode === 'search' && (
									<CCol md={8} className="px-0">
										<AsyncSelect
											cacheOptions={false}
											defaultOptions={false}
											loadOptions={loadUserOptions}
											value={selectedUser}
											onChange={handleAsyncUserChange}
											isClearable
											placeholder="Введіть ім'я або username"
											styles={errors.user ? {
												control: (base) => ({ ...base, borderColor: '#dc3545' }),
											} : undefined}
										/>
									</CCol>
								)}

								{!confirmedUser && userInputMode === 'manual' && (
									<CCol md={8} className="px-0">
										<CInputGroup>
											<CFormInput
												type="number"
												placeholder="ID користувача"
												value={manualUserId}
												onChange={(e) => setManualUserId(e.target.value)}
												invalid={!!errors.user && !userId}
												disabled={manualConfirming}
											/>
											<CButton
												color="secondary"
												onClick={handleManualConfirm}
												disabled={manualConfirming || !manualUserId}
												title="Знайти користувача за ID"
											>
												{manualConfirming ? 'Пошук...' : 'Підтвердити'}
											</CButton>
										</CInputGroup>
									</CCol>
								)}

								{confirmedUser && (
									<div className="d-flex align-items-center gap-2 mt-1">
										<span className="text-success fw-semibold">✓ {confirmedUserLabel}</span>
										<CButton
											color="secondary"
											variant="outline"
											size="sm"
											onClick={handleClearUser}
											title="Скинути вибраного користувача"
										>
											× Скинути
										</CButton>
									</div>
								)}
							</CCol>

							{/* Заголовок */}
							<CCol md={8}>
								<CFormLabel>Заголовок *</CFormLabel>
								<CFormInput
									value={title}
									onChange={(e) => {
										setTitle(e.target.value);
										if (errors.title) setErrors(prev => ({ ...prev, title: false }));
									}}
									autoComplete="off"
									invalid={!!errors.title}
								/>
							</CCol>

							{/* Тип повідомлення */}
							<CCol md={4}>
								<CFormLabel>Тип повідомлення</CFormLabel>
								<CFormSelect
									value={type}
									onChange={(e) => setType(e.target.value)}
								>
									{TYPE_OPTIONS.map(o => (
										<option key={o.value} value={o.value}>{o.label}</option>
									))}
								</CFormSelect>
							</CCol>

							{/* Текст */}
							<CCol xs={12}>
								<CFormLabel>Текст *</CFormLabel>
								<CFormTextarea
									rows={8}
									value={body}
									onChange={(e) => {
										setBody(e.target.value);
										if (errors.body) setErrors(prev => ({ ...prev, body: false }));
									}}
									invalid={!!errors.body}
								/>
							</CCol>
						</CRow>

						<div className="d-flex justify-content-start mt-4">
							<CButton
								color="secondary"
								variant="outline"
								onClick={handleBack}
								title="Повернутись до попередньої сторінки"
							>
								← Назад
							</CButton>
							<CButton
								color="success"
								className="ms-2"
								onClick={handleSubmit}
								disabled={submitting}
								title="Надіслати сповіщення"
							>
								{submitting ? 'Надсилання...' : 'Створити'}
							</CButton>
						</div>
					</FormSubmitOverlay>
				</CCardBody>
			</CCard>
		</>
	);
};

export default UsersNotificationsForm;
